import {
  Card,
  Col,
  Descriptions,
  Popover,
  Row,
  Tag,
  Timeline,
  Typography,
} from "antd"
import styles from "./styles.module.scss"
import { useLocation, useNavigate } from "react-router-dom"
import { useEventId } from ".."
import { useMember } from "../../queries"
import { formatDay, formatTimeToCurrentTimezone } from "utils/format-datetime"
import Loader from "components/loader"
import { ReactComponent as AccidentIcon } from "assets/icons/fall-accident.svg"
import RatingEmoji from "components/rating-emojy"
import Layout from "modules/layout/dashboard"
import { useMemberId } from "pages/member"
import { useTranslation } from "react-i18next"
import Link from "antd/es/typography/Link"
import moment from "moment"
import HTMLReactParser from "html-react-parser"
import { useEvent, useMemberFeedback } from "./queries"
import { IAttendance } from "./types"
import { formatCurrency } from "utils/format-currency"

const { Title, Text } = Typography

const { Main } = Layout

interface ContentProps extends IAttendance {
  time: string
}

const ShowEvent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const eventId = useEventId()
  const memberId = useMemberId()
  const member = useMember(memberId)
  const event = useEvent(eventId!)
  const PopoverContent: React.FC<ContentProps> = ({
    accompanied_by,
    time,
    displayed_accompanied_by,
    respondant_name,
    other_note,
  }) => (
    <div style={{ width: 300 }}>
      <Descriptions column={1}>
        <Descriptions.Item label={t("date.time")}>
          {moment(time).utcOffset(0, false).format("HH:mm")}
        </Descriptions.Item>
        <Descriptions.Item label={t("events.accompanied_by")}>
          {`${displayed_accompanied_by} ${(() => {
            switch (accompanied_by) {
              case "respondent":
                return " - " + respondant_name
              case "other":
                return " - " + other_note
              default:
                return ""
            }
          })()}`}
        </Descriptions.Item>
      </Descriptions>
    </div>
  )
  const memberFeedback = useMemberFeedback(memberId!, eventId!, {
    select(data): any {
      const accidents: any[] = []
      const attendances: any[] = []
      data.accidents?.forEach((element) =>
        accidents.push({
          color: "var(--color-error)",
          dot: <AccidentIcon />,
          time: element.accident_at,
          children: (
            <Link onClick={() => navigate(`accident/${element.id}`)}>
              {`Accident ${
                element?.accident_at
                  ? formatTimeToCurrentTimezone(element.accident_at)
                  : ""
              }`}
            </Link>
          ),
        })
      )
      data.attendances?.forEach((element) => {
        let time =
          element.type === "checkin" ? element.arrived_at : element.left_at
        attendances.push({
          time: time,
          children: (
            <Popover content={<PopoverContent time={time!} {...element!} />}>
              {`${t(`events.${element.type}`)} ${moment(time)
                .utcOffset(0, false)
                .format("HH:mm")} `}
            </Popover>
          ),
        })
      })
      return { ...data, accidents: accidents, attendances: attendances }
    },
  })

  const isLoading = event.isLoading || memberFeedback.isLoading
  return (
    <div className={styles.container}>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <Main.PageHeader
            onButtonClick={
              location.key === "default"
                ? () => navigate(`/members/${memberId}/events/subscriptions`)
                : undefined
            }
            breadcrumbs={[
              {
                title: member.data?.name,
                link: `/members/${member.data?.id}/events`,
              },
              {
                title: t("events.title"),
                link: `/members/${member.data?.id}/events`,
              },
              {
                title: event.data?.name,
              },
            ]}
            title={<Title level={3}>{event.data?.name}</Title>}
            subTitle={
              <Text>
                {`${formatDay(event.data?.start_date)}, ${
                  event.data?.start_hour
                }h - ${event.data?.end_hour}h`}
              </Text>
            }
          />
          <Card>
            <Row style={{ marginBottom: 10 }}>
              <Col md={11}>
                <img
                  style={{ height: 250, width: "100%" }}
                  alt="example"
                  src={
                    event.data?.image ?? require("assets/placeholder_image.jpg")
                  }
                />
              </Col>
              <Col md={11} offset={2}>
                <Descriptions
                  column={1}
                  className={styles.description}
                  title={t("events.event_informations")}
                >
                  <Descriptions.Item label={t("date.date")}>
                    {event.data?.start_date === event.data?.end_date
                      ? event.data?.start_date
                      : t("events.from_to", {
                          start: event.data?.start_date,
                          end: event.data?.end_date,
                        })}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("date.hours")}>
                    {`${event.data?.start_hour}h - ${event.data?.end_hour}h`}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("events.available_places")}>
                    {event.data?.available_seats}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("events.price")}>
                    {typeof event.data?.activity_unit_price_offer !==
                      "number" && !event.data?.activity_unit_price_offer ? (
                      <Tag color="success">{t("global.free")}</Tag>
                    ) : (
                      `${
                        event.data?.activity_unit_price_offer
                          ? formatCurrency(
                              event.data?.activity_unit_price_offer!
                            )
                          : ""
                      } / ${event.data?.displayed_enrollment}`
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("events.place")}>
                    {event.data?.place?.name ?? event.data?.activity_room?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("events.subscription_type")}>
                    {event.data?.displayed_enrollment}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("global.description")}>
                    {event.data?.description}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("global.detail")}>
                    {event.data?.comment}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
            {event.data?.status === "closed" && (
              <Row>
                <>
                  <Col md={11}>
                    <Card style={{ marginBottom: 15 }}>
                      <Descriptions
                        column={1}
                        className={styles.description}
                        title={t("events.member_presence")}
                      >
                        <Descriptions.Item label={t("events.absent")}>
                          {memberFeedback.data?.is_absent
                            ? t("global.yes")
                            : t("global.no")}
                        </Descriptions.Item>
                        {!memberFeedback.data?.attendence_on_execution && (
                          <Descriptions.Item
                            label={t("events.justified_abscence")}
                          >
                            {memberFeedback.data?.planned_absent
                              ? t("global.yes")
                              : t("global.no")}
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item
                          label={t("events.present_without_subscription")}
                        >
                          {memberFeedback.data?.attendence_on_execution
                            ? t("global.yes")
                            : t("global.no")}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    {!memberFeedback.data?.is_absent && (
                      <Card>
                        <Descriptions
                          className={styles.description}
                          column={1}
                          title={t("events.monitors_comments")}
                        >
                          <Descriptions.Item label={t("events.general")}>
                            {memberFeedback.data?.presence_emoji && (
                              <RatingEmoji
                                fontSize={20}
                                rating={memberFeedback.data?.presence_emoji}
                              />
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("global.detail")}>
                            {HTMLReactParser(
                              memberFeedback.data?.presence_feedback ?? ""
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("events.progress")}>
                            <Timeline
                              style={{ marginTop: 8, marginLeft: 30 }}
                              items={(
                                [
                                  ...memberFeedback.data?.attendances!,
                                  ...memberFeedback.data?.accidents!,
                                ] as any
                              ).sort(
                                (a: any, b: any) =>
                                  moment(a.time).valueOf() -
                                  moment(b.time).valueOf()
                              )}
                            />
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    )}
                  </Col>
                  <Col md={11} offset={2}>
                    {!memberFeedback.data?.is_absent && (
                      <Card>
                        <Descriptions
                          className={styles.description}
                          column={1}
                          title={t("events.activity_progress")}
                        >
                          <Descriptions.Item label={t("events.general")}>
                            {memberFeedback.data?.event_emoji && (
                              <RatingEmoji
                                fontSize={20}
                                rating={memberFeedback.data!.event_emoji}
                              />
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("global.detail")}>
                            {HTMLReactParser(
                              memberFeedback.data?.event_feedback ?? ""
                            )}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    )}
                  </Col>
                </>
              </Row>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}

export default ShowEvent
